import React from "react";
import Table from "../../common/Table";
import SaveBtn from "../../common/SaveBtn";
import Pagination from "../../common/Pagination";
import useRDOInvite, {
  CSV_ALLOCATAIRE_FIELDS,
  VALIDATION_ALLOCATAIRE_FIELDS,
  STATUTS,
} from "./useRDOInvite";

// MODE DÉVELOPPEMENT : true = bouton "Valider" toujours visible pour tests, false = comportement normal
const DEV_MODE = true;

const RDOInviteImport = () => {
  const {
    uploadedFile,
    csvData,
    validatedData,
    isValidating,
    isSending,
    step,
    validCount,
    errorCount,
    validationPage,
    validationFilter,
    validationNbPages,
    paginatedValidatedData,
    handleDownloadTemplate,
    handleFileUpload,
    handlePreValidation,
    handleDownloadValidation,
    handleSendInvitations,
    handleReset,
    handleValidationPageChange,
    handleValidationFilterChange,
  } = useRDOInvite();

  // Configuration du tableau pour la validation
  const validationTableFields = [
    {
      name: "NIR",
      path: CSV_ALLOCATAIRE_FIELDS.nir,
    },
    {
      name: "Nom",
      path: VALIDATION_ALLOCATAIRE_FIELDS.nom,
      render: (nom) => nom || "-",
    },
    {
      name: "Prénom",
      path: VALIDATION_ALLOCATAIRE_FIELDS.prenom,
      render: (prenom) => prenom || "-",
    },
    {
      name: "Date de naissance",
      path: VALIDATION_ALLOCATAIRE_FIELDS.dateNaissance,
      render: (date) => date || "-",
    },
    {
      name: "Email",
      path: VALIDATION_ALLOCATAIRE_FIELDS.email,
      render: (email) => email || "-",
    },
    {
      name: "Orientation",
      path: VALIDATION_ALLOCATAIRE_FIELDS.orientation,
      render: (orientation) => orientation || "-",
    },
    {
      name: "Date orientation",
      path: VALIDATION_ALLOCATAIRE_FIELDS.dateOrientation,
      render: (date) => date || "-",
    },
    {
      name: "SDD/NSDD",
      path: VALIDATION_ALLOCATAIRE_FIELDS.typeDroitsDevoirs,
      render: (type) => {
        if (!type) return "-";
        return (
          <span className={`badge badge-${type === "SDD" ? "info" : "light"}`}>
            {type}
          </span>
        );
      },
    },
    {
      name: "Statut",
      path: VALIDATION_ALLOCATAIRE_FIELDS.statut,
      render: (statut) => (
        <span
          className={`badge badge-${
            statut === STATUTS.VALIDE ? "success" : "danger"
          }`}
        >
          {statut === STATUTS.VALIDE ? "Valide" : "Erreur"}
        </span>
      ),
    },
    {
      name: "Erreur",
      path: VALIDATION_ALLOCATAIRE_FIELDS.erreur,
      render: (erreur) => <span className="text-danger">{erreur || "-"}</span>,
    },
  ];

  return (
    <div>
      {/* Étape 1: Téléchargement du gabarit */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-download mr-2"></i>
            Étape 1 : Télécharger le gabarit
          </h5>
          <p className="text-muted">
            Téléchargez le gabarit, collez-y la liste des NIR des allocataires
            à inviter (un NIR à 15 chiffres par ligne, sous l'en-tête{" "}
            <strong>nir</strong>) et enregistrez-le au format CSV séparé par des
            points-virgules.
          </p>

          {/* Nouveauté : une seule colonne */}
          <div className="alert alert-success">
            <h6 className="alert-heading mb-2">
              <i className="fa fa-bullhorn mr-2"></i>
              Nouveau depuis le 21/09/2026 : une seule colonne suffit
            </h6>
            <p className="mb-1">
              Pour simplifier la préparation du fichier, il ne contient plus
              que la colonne <strong>nir</strong>. Le nom, le prénom et la date
              de naissance ne sont plus demandés : ils sont retrouvés
              automatiquement dans ESPOAR à partir du NIR.
            </p>
            <p className="mb-0">
              L'enregistrement en « CSV UTF-8 » n'est plus nécessaire, un CSV
              classique suffit. Les anciens fichiers à quatre colonnes restent
              acceptés.
            </p>
          </div>

          {/* Bloc informatif pour Excel */}
          <div className="alert alert-info mb-0">
            <h6 className="alert-heading mb-2">
              <i className="fa fa-info-circle mr-2"></i>
              Comment enregistrer au bon format depuis Excel
            </h6>
            <ol className="mb-0 pl-3">
              <li>
                Vérifiez que la colonne des NIR est au{" "}
                <strong>format Texte</strong> (clic droit sur la colonne &gt;
                Format de cellule &gt; Texte). Si Excel affiche un NIR sous la
                forme <code>1,97E+14</code>, il sera illisible et la ligne sera
                rejetée.
              </li>
              <li>
                Accédez à <strong>Fichier &gt; Enregistrer sous</strong>
              </li>
              <li>
                Dans la zone <strong>Type de fichier</strong>, choisissez{" "}
                <strong>
                  CSV (délimité par des <u>points-virgules</u>)
                </strong>
              </li>
              <li>
                En cas de doute, ouvrez le fichier enregistré avec le
                Bloc-notes : chaque ligne doit montrer un NIR à 15 chiffres.
              </li>
            </ol>
          </div>

          <button
            className="btn btn-outline-primary mt-3"
            onClick={handleDownloadTemplate}
          >
            <i className="fa fa-download mr-2"></i>
            Télécharger le gabarit CSV
          </button>
        </div>
      </div>

      {/* Étape 2: Upload du fichier */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">
            <i className="fa fa-upload mr-2"></i>
            Étape 2 : Uploader le fichier CSV
          </h5>
          <p className="text-muted">
            Sélectionnez le fichier CSV contenant la liste des allocataires à
            inviter
          </p>
          <div className="custom-file mb-3">
            <input
              type="file"
              className="custom-file-input"
              id="csvFileInput"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={step === "sent"}
            />
            <label className="custom-file-label" htmlFor="csvFileInput">
              {uploadedFile ? uploadedFile.name : "Choisir un fichier CSV..."}
            </label>
          </div>
          {csvData.length > 0 && (
            <div className="alert alert-info">
              <i className="fa fa-info-circle mr-2"></i>
              {csvData.length} allocataire(s) chargé(s)
            </div>
          )}
          {csvData.length > 0 &&
            (step === "upload" || (DEV_MODE && step === "validation")) && (
              <SaveBtn
                className="btn-primary"
                typeBtn="button"
                isSaving={isValidating}
                save={handlePreValidation}
                text="Valider les données"
              />
            )}
        </div>
      </div>

      {/* Étape 3: Visualisation de la validation */}
      {step === "validation" && validatedData.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">
              <i className="fa fa-check-circle mr-2"></i>
              Étape 3 : Vérification des données
            </h5>
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="alert alert-success">
                  <strong>{validCount}</strong> allocataire(s) valide(s)
                </div>
              </div>
              <div className="col-md-6">
                <div className="alert alert-danger">
                  <strong>{errorCount}</strong> allocataire(s) en erreur
                </div>
              </div>
            </div>

            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <button
                  className="btn btn-secondary mr-2"
                  onClick={handleDownloadValidation}
                >
                  <i className="fa fa-download mr-2"></i>
                  Télécharger le rapport de validation
                </button>
              </div>

              {/* Filtres par statut */}
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    validationFilter === "tous"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => handleValidationFilterChange("tous")}
                >
                  Tous ({validatedData.length})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    validationFilter === "valide"
                      ? "btn-success"
                      : "btn-outline-success"
                  }`}
                  onClick={() => handleValidationFilterChange("valide")}
                >
                  Valides ({validCount})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    validationFilter === "erreur"
                      ? "btn-danger"
                      : "btn-outline-danger"
                  }`}
                  onClick={() => handleValidationFilterChange("erreur")}
                >
                  Erreurs ({errorCount})
                </button>
              </div>
            </div>

            <Table
              datas={paginatedValidatedData}
              fields={validationTableFields}
              fullWidth={true}
            />

            {/* Pagination */}
            <Pagination
              nbPages={validationNbPages}
              page={validationPage}
              changePage={handleValidationPageChange}
              nbItemsByPage={25}
              changeNbItemsByPage={() => {}} // Pas de changement du nombre d'items
            />

            <div className="mt-4 d-flex justify-content-between">
              <button
                className="btn btn-outline-secondary"
                onClick={handleReset}
              >
                <i className="fa fa-undo mr-2"></i>
                Recommencer
              </button>
              <SaveBtn
                className="btn-success"
                typeBtn="button"
                isSaving={isSending}
                save={handleSendInvitations}
                text={`Envoyer les invitations (${validCount} allocataire${
                  validCount > 1 ? "s" : ""
                })`}
                disabled={validCount === 0}
              />
            </div>
          </div>
        </div>
      )}

      {/* Étape 4: Confirmation d'envoi */}
      {step === "sent" && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="alert alert-success">
              <h5>
                <i className="fa fa-check-circle mr-2"></i>
                Import en cours
              </h5>
              <p className="mb-0">
                Les invitations sont en cours de traitement. Les allocataires
                valides recevront un email d'invitation prochainement.
              </p>
            </div>
            <button className="btn btn-primary" onClick={handleReset}>
              <i className="fa fa-plus mr-2"></i>
              Nouvelle invitation
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RDOInviteImport;
